import {
  allergiesOptions,
  allergiesNameMapper,
} from "../../pages/Student/OrderPage/constants/allergies";

export const EMPTY_STATE = {
  food_station_id: null,
  sub_station_id: null,
  dining_option_id: null,
  options_id: [],
  name: null,
  surname: null,
  allergies: null,
  special_note: "",
  notification: null,
};

export const EMPTY_RUNTIME = {
  menuGroupIndex: 0,
  specialNoteAsked: false,
  pushSubscribed: false,
  telegramChecked: false,
  emailExplicit: false,
  phoneExplicit: false,
  nameConfirmed: false,
  surnameConfirmed: false,
  showAllergenItems: false,
  allergiesConfirmed: false,
  flow: "new",
  orderMode: null,
  voiceMode: false,
};

export const TEXT_STEPS = new Set([
  "ask_name",
  "ask_surname",
  "ask_notification_email",
  "ask_notification_phone",
  "ask_special_note",
]);

export const PIPELINE = [
  "ask_order_mode",
  "pick_station",
  "pick_sub_station",
  "pick_dining",
  "confirm_allergies",
  "ask_allergies",
  "pick_menu_group",
  "confirm_name",
  "ask_name",
  "confirm_surname",
  "ask_surname",
  "ask_notification_type",
  "confirm_notification_email",
  "ask_notification_email",
  "confirm_notification_phone",
  "ask_notification_phone",
  "subscribe_push",
  "check_telegram",
  "ask_special_note",
  "summary",
];

export const NOTIFICATION_LABELS = {
  push: "Push notification",
  email: "Email",
  text: "Text message",
  telegram: "Telegram",
};

export const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export const splitFullName = (fullName) => {
  if (!fullName || typeof fullName !== "string") return ["", ""];
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return ["", ""];
  if (parts.length === 1) return [parts[0], ""];
  return [parts[0], parts.slice(1).join(" ")];
};

export const applyCustomerPrefill = (state, customer) => {
  if (!customer) return state;
  const next = { ...state };
  if (next.name == null && customer.first_name) next.name = customer.first_name;
  if (next.surname == null && customer.last_name) next.surname = customer.last_name;
  if (Array.isArray(customer.allergies) && next.allergies === null) {
    next.allergies = customer.allergies;
  }
  return next;
};

export const applyRepeatSuggestion = (state, suggestion) => {
  if (!suggestion) return state;
  return {
    ...state,
    food_station_id: suggestion.food_station_id ?? state.food_station_id,
    sub_station_id: suggestion.sub_station_id ?? state.sub_station_id,
    dining_option_id: suggestion.dining_option_id ?? state.dining_option_id,
    options_id: Array.isArray(suggestion.options_id)
      ? [...suggestion.options_id]
      : state.options_id,
  };
};

export const getStationById = (ctx, id) =>
  (ctx.stations || []).find((s) => +s.id === +id) || null;

export const getSubStationById = (ctx, stationId, subId) => {
  const st = getStationById(ctx, stationId);
  return (st?.sub_stations || []).find((s) => +s.id === +subId) || null;
};

export const getDiningById = (ctx, id) =>
  (ctx.diningOptions || []).find((d) => +d.id === +id) || null;

export const getMenuGroups = (ctx) => ctx.stationOptions || [];

export const getItemById = (groups, id) => {
  for (const g of groups || []) {
    const found = (g.items || []).find((i) => +i.id === +id);
    if (found) return { item: found, group: g.group };
  }
  return null;
};

const isItemVisible = (item) => {
  if (!item) return false;
  if (item.isDeleted === 1) return false;
  if (item.visible !== undefined && item.visible !== 1 && item.visible !== true)
    return false;
  return true;
};

const isItemAllowed = (item, allergies) => {
  if (!Array.isArray(allergies) || allergies.length === 0) return true;
  if (allergies.length === 1 && allergies[0] === "none") return true;
  const itemAllergens = Array.isArray(item.allergens)
    ? item.allergens.map((a) => String(a))
    : [];
  return !itemAllergens.some((a) => allergies.map(String).includes(a));
};

export const getAllowedItems = (group, allergies) =>
  (group?.items || []).filter(
    (it) => isItemVisible(it) && isItemAllowed(it, allergies),
  );

export const classifyRepeatItems = (ctx, optionIds) => {
  const groups = getMenuGroups(ctx);
  const valid = [];
  const disabledNames = [];
  let removedCount = 0;
  (optionIds || []).forEach((id) => {
    const found = getItemById(groups, id);
    if (found && isItemVisible(found.item)) {
      valid.push(id);
    } else if (found) {
      disabledNames.push(found.item.name);
    } else {
      removedCount++;
    }
  });
  return { valid, disabledNames, removedCount };
};

export const isSuggestionRepeatable = (ctx) =>
  !!ctx.suggestion && !!getStationById(ctx, ctx.suggestion.food_station_id);

const getActiveMenuGroup = (ctx, runtime) =>
  getMenuGroups(ctx)[runtime.menuGroupIndex] || null;

export const isStepNeeded = (stepId, ctx, state, runtime) => {
  switch (stepId) {
    case "ask_order_mode":
      return runtime.orderMode == null;
    case "pick_station":
      return state.food_station_id == null;
    case "pick_sub_station": {
      const st = getStationById(ctx, state.food_station_id);
      return (
        !!st?.is_multi &&
        (st?.sub_stations?.length || 0) > 0 &&
        state.sub_station_id == null
      );
    }
    case "pick_dining":
      return state.dining_option_id == null;
    case "pick_menu_group":
      if (runtime.flow === "repeat") return false;
      return runtime.menuGroupIndex < getMenuGroups(ctx).length;
    case "confirm_allergies": {
      // Confirm only saved, not-yet-confirmed *real* allergies (skip "none").
      if (runtime.allergiesConfirmed) return false;
      if (!Array.isArray(state.allergies)) return false;
      return state.allergies.some((a) => a !== "none");
    }
    case "ask_allergies":
      // Ask when nothing is on file, or the saved value is empty / "none".
      if (runtime.allergiesConfirmed) return state.allergies == null;
      return (
        state.allergies == null ||
        (Array.isArray(state.allergies) &&
          !state.allergies.some((a) => a !== "none"))
      );
    case "confirm_name":
      return false;
    case "ask_name":
      return state.name == null;
    case "confirm_surname":
      return false;
    case "ask_surname":
      return state.surname == null;
    case "ask_notification_type":
      return !state.notification || !state.notification.type;
    case "confirm_notification_email":
      return (
        state.notification?.type === "email" &&
        !state.notification?.email &&
        !!ctx.customer?.email &&
        !runtime.emailExplicit
      );
    case "ask_notification_email":
      return (
        state.notification?.type === "email" && !state.notification?.email
      );
    case "confirm_notification_phone":
      return (
        state.notification?.type === "text" &&
        !state.notification?.phone &&
        !!ctx.customer?.phone &&
        !runtime.phoneExplicit
      );
    case "ask_notification_phone":
      return (
        state.notification?.type === "text" && !state.notification?.phone
      );
    case "subscribe_push":
      return (
        state.notification?.type === "push" &&
        !runtime.pushSubscribed &&
        !state.notification?.endpoint
      );
    case "check_telegram":
      return (
        state.notification?.type === "telegram" && !runtime.telegramChecked
      );
    case "ask_special_note":
      return !runtime.specialNoteAsked;
    case "summary":
      return true;
    default:
      return false;
  }
};

export const pickNextStep = (ctx, state, runtime) => {
  if (runtime.voiceMode) {
    for (const id of PIPELINE) {
      if (id === "summary" || TEXT_STEPS.has(id)) continue;
      if (isStepNeeded(id, ctx, state, runtime)) return id;
    }
    for (const id of PIPELINE) {
      if (!TEXT_STEPS.has(id)) continue;
      if (isStepNeeded(id, ctx, state, runtime)) return id;
    }
    return "summary";
  }
  for (const id of PIPELINE) {
    if (isStepNeeded(id, ctx, state, runtime)) return id;
  }
  return "summary";
};

export const formatSuggestionText = (suggestion, ctx) => {
  if (!suggestion) return "";
  const lines = [];
  const st = getStationById(ctx, suggestion.food_station_id);
  if (st) lines.push(`Station: **${st.name}**`);
  if (st?.is_multi && suggestion.sub_station_id) {
    const sub = getSubStationById(
      ctx,
      suggestion.food_station_id,
      suggestion.sub_station_id,
    );
    if (sub) lines.push(`Sub-station: **${sub.name}**`);
  }
  if (Array.isArray(suggestion.options_id) && suggestion.options_id.length) {
    const groups = getMenuGroups(ctx);
    const byGroup = new Map();
    let unknownCount = 0;
    suggestion.options_id.forEach((id) => {
      const found = getItemById(groups, id);
      if (found) {
        const groupName = found.group?.group_name || "Items";
        if (!byGroup.has(groupName)) byGroup.set(groupName, []);
        byGroup.get(groupName).push(found.item.name);
      } else {
        unknownCount++;
      }
    });
    if (byGroup.size > 0) {
      byGroup.forEach((itemNames, groupName) => {
        lines.push(`${groupName}:`);
        itemNames.forEach((name) => lines.push(`  • **${name}**`));
      });
      if (unknownCount > 0) lines.push(`  • **+${unknownCount} more**`);
    } else {
      lines.push(`Items: **${suggestion.options_id.length}**`);
    }
  }
  if (suggestion.count > 1) {
    lines.push(`Ordered **${suggestion.count}** times`);
  }
  return lines.join("\n");
};

export const formatSummary = (state, ctx) => {
  const lines = ["Here's what you're getting:"];
  const st = getStationById(ctx, state.food_station_id);
  if (st) lines.push(`• Station: **${st.name}**`);
  if (st?.is_multi && state.sub_station_id) {
    const sub = getSubStationById(
      ctx,
      state.food_station_id,
      state.sub_station_id,
    );
    if (sub) lines.push(`• Sub-station: **${sub.name}**`);
  }
  const dining = getDiningById(ctx, state.dining_option_id);
  if (dining) lines.push(`• Dining: **${dining.name}**`);

  const groups = getMenuGroups(ctx);
  if (state.options_id?.length && groups.length) {
    const itemNames = state.options_id
      .map((id) => getItemById(groups, id)?.item?.name)
      .filter(Boolean);
    if (itemNames.length) {
      lines.push(`• Items: **${itemNames.join(", ")}**`);
    }
  } else if (state.options_id?.length) {
    lines.push(`• Items: **${state.options_id.length} selected**`);
  }

  if (Array.isArray(state.allergies) && state.allergies.length) {
    const names = state.allergies
      .map((a) => allergiesNameMapper[a] || a)
      .join(", ");
    lines.push(`• Allergies: **${names}**`);
  }

  if (state.name) lines.push(`• First name: **${state.name}**`);
  if (state.surname) lines.push(`• Last name: **${state.surname}**`);

  if (state.notification?.type) {
    const label =
      NOTIFICATION_LABELS[state.notification.type] ||
      cap(state.notification.type);
    const detail = state.notification.email
      ? ` (${state.notification.email})`
      : state.notification.phone
        ? ` (+1${state.notification.phone})`
        : "";
    lines.push(`• Notification: **${label}${detail}**`);
  }

  if (state.special_note) {
    lines.push(`• Note: **${state.special_note}**`);
  }

  return lines.join("\n");
};

export const getStepView = (stepId, ctx, state, runtime) => {
  switch (stepId) {
    case "ask_order_mode": {
      return {
        messages: [
          "How would you like to order?",
          "Type it yourself, or go hands-free and just speak.",
        ],
        chips: [
          { label: "Type my order", value: "__text__" },
          { label: "Order by voice", value: "__voice__", variant: "confirm" },
        ],
      };
    }
    case "welcome": {
      const firstName = ctx.customer?.first_name || "";
      const greeting = firstName ? `Hey ${firstName}, welcome back! 👋` : "Hey there! Welcome back!";


      if ((ctx.stations || []).length === 0) {
        return {
          messages: [
            greeting,
            "Hmm, no stations are open right now. Check back soon!",
          ],
          chips: [],
        };
      }

      if (isSuggestionRepeatable(ctx)) {
        return {
          messages: [
            `${greeting} Want to order the same thing as last time?`,
            { text: formatSuggestionText(ctx.suggestion, ctx), card: true },
          ],
          chips: [
            { label: "Repeat last order", value: "__repeat__" },
            { label: "Make a new order", value: "__new__" },
          ],
        };
      }
      return {
        messages: [`${greeting} Awesome, let's get your order going!`],
        chips: [],
        autoAdvance: true,
      };
    }
    case "pick_station": {
      const stations = ctx.stations || [];
      if (stations.length === 0) {
        return {
          messages: ["Hmm, no stations are open right now. Check back soon!"],
          chips: [],
        };
      }
      if (stations.length === 1) {
        return {
          messages: [`Looks like **${stations[0].name}** is the only station open — I'll set that up for you!`],
          chips: [],
          autoAdvance: true,
          autoValue: stations[0].id,
        };
      }
      return {
        messages: ["Which station are you ordering from today?"],
        chips: stations.map((s) => ({ label: s.name, value: s.id })),
      };
    }
    case "pick_sub_station": {
      const st = getStationById(ctx, state.food_station_id);
      const subs = st?.sub_stations || [];
      return {
        messages: [`${st?.name || "Station"} has a few sub-stations to choose from. Which one?`],
        chips: subs.map((s) => ({ label: s.name, value: s.id })),
      };
    }
    case "pick_dining": {
      const dining = ctx.diningOptions || [];
      if (dining.length === 0) {
        return {
          messages: ["Hang on, loading dining options..."],
          chips: [],
          waiting: true,
        };
      }
      if (dining.length === 1) {
        return {
          messages: [`Looks like **${dining[0].name}** is the only option here — easy choice!`],
          chips: [],
          autoAdvance: true,
          autoValue: dining[0].id,
        };
      }
      return {
        messages: ["How do you want to get your food?"],
        chips: dining.map((d) => ({ label: d.name, value: d.id })),
      };
    }
    case "pick_menu_group": {
      const groups = getMenuGroups(ctx);
      const group = groups[runtime.menuGroupIndex];
      if (!group) {
        return { messages: [], chips: [], autoAdvance: true };
      }
      const groupName = group.group.group_name;
      const allAllowed = getAllowedItems(group, []);
      // No items at all → silently skip this group
      if (allAllowed.length === 0) {
        return { messages: [], chips: [], autoAdvance: true };
      }
      const safeAllowed = getAllowedItems(group, state.allergies);
      const hasAllergenItems = allAllowed.length > safeAllowed.length;
      const hasRealAllergies =
        Array.isArray(state.allergies) &&
        state.allergies.length > 0 &&
        !(state.allergies.length === 1 && state.allergies[0] === "none");
      const effectiveAllowed = runtime.showAllergenItems
        ? allAllowed
        : safeAllowed;
      const isMulti = !!group.group.allow_multiple;
      const max = group.group.max_multiple || 1;
      const toggleChips =
        hasAllergenItems && hasRealAllergies
          ? [
              {
                label: runtime.showAllergenItems
                  ? `Hide ${groupName} items with my allergens`
                  : `Show ${groupName} items with my allergens`,
                value: "__toggle_allergens__",
                action: "toggle_allergens",
              },
            ]
          : [];
      // All items filtered out by allergies → explicit notice
      if (effectiveAllowed.length === 0) {
        return {
          messages: [
            `None of the ${groupName} options work with your allergies, sorry!`,
            hasAllergenItems
              ? "You can still see items with your allergens if you want."
              : "Nothing else to pick here, moving on!",
          ],
          chips: toggleChips,
          items: [],
          multi: isMulti,
          multiMax: max,
        };
      }
      if (effectiveAllowed.length === 1 && toggleChips.length === 0) {
        return {
          messages: [`Only one choice for ${groupName} — **${effectiveAllowed[0].name}**. Done!`],
          chips: [],
          autoAdvance: true,
          autoValue: isMulti ? [effectiveAllowed[0].id] : effectiveAllowed[0].id,
        };
      }
      return {
        messages: [
          isMulti
            ? `Pick your ${groupName} (up to ${max}):`
            : `What ${groupName} would you like?`,
        ],
        chips: toggleChips,
        items: effectiveAllowed.map((it) => ({
          label: it.name,
          value: it.id,
          image_name: it.image_name,
        })),
        multi: isMulti,
        multiMax: max,
      };
    }
    case "confirm_allergies": {
      const list = (state.allergies || [])
        .map((a) => allergiesNameMapper[a] || a)
        .join(", ");
      return {
        messages: [
          `You're allergic to: ${list}. Still the same?`,
          "Is that still right?",
        ],
        chips: [
          { label: "Yes, keep them", value: "__yes__" },
          { label: "Change them", value: "__change__" },
        ],
      };
    }
    case "ask_allergies": {
      return {
        messages: [
          "Any allergies I should know about?",
          "Pick everything that applies, then hit Done.",
        ],
        chips: allergiesOptions.map((a) => ({
          label: a.label,
          value: a.value,
        })),
        multi: true,
        noneValue: "none",
      };
    }
    case "confirm_name": {
      const first = splitFullName(ctx.customer?.full_name || "")[0];
      return {
        messages: [`Got **${first}** on file for you — want to use that?`],
        chips: [
          { label: `Use ${first}`, value: "__use_default__" },
          { label: "Use different first name", value: "__use_other__" },
        ],
      };
    }
    case "ask_name": {
      return {
        messages: ["What's your first name?"],
        input: {
          placeholder: "First name",
          initialValue: "",
        },
      };
    }
    case "confirm_surname": {
      const last = splitFullName(ctx.customer?.full_name || "")[1];
      return {
        messages: [`Got **${last}** on file — still good?`],
        chips: [
          { label: `Use ${last}`, value: "__use_default__" },
          { label: "Use different last name", value: "__use_other__" },
        ],
      };
    }
    case "ask_surname": {
      return {
        messages: ["What's your last name?"],
        input: {
          placeholder: "Last name",
          initialValue: "",
        },
      };
    }
    case "ask_notification_type": {
      const opts = ctx.notificationOptions || [];
      const savedChannels = ctx.customer?.notification_channels;
      const enabledChannels = Array.isArray(savedChannels)
        ? savedChannels
        : null;
      const available = opts.filter((o) => {
        if (o.title === "telegram" && !ctx.customer?.telegram_chat_id) {
          return false;
        }
        if (enabledChannels && !enabledChannels.includes(o.title)) {
          return false;
        }
        return true;
      });
      if (available.length === 0) {
        return {
          messages: ["No notification options set up yet, sorry!"],
          chips: [],
        };
      }
      return {
        messages: ["How should I let you know when your order's ready?"],
        chips: available.map((o) => ({
          label: NOTIFICATION_LABELS[o.title] || cap(o.title),
          value: o.title,
        })),
      };
    }
    case "confirm_notification_email": {
      return {
        messages: [
          `I've got **${ctx.customer?.email}** for you — want to use that?`,
        ],
        chips: [
          { label: `Use ${ctx.customer?.email}`, value: "__use_default__" },
          { label: "Use different email", value: "__use_other__" },
        ],
      };
    }
    case "ask_notification_email": {
      return {
        messages: ["What's your email?"],
        input: {
          placeholder: "you@example.com",
          initialValue: "",
        },
      };
    }
    case "confirm_notification_phone": {
      const phone = ctx.customer?.phone;
      return {
        messages: [`I've got **${phone}** saved — want to use that?`],
        chips: [
          { label: `Use ${phone}`, value: "__use_default__" },
          { label: "Use different number", value: "__use_other__" },
        ],
      };
    }
    case "ask_notification_phone": {
      return {
        messages: ["What's your phone number? (10 digits)"],
        input: {
          placeholder: "10-digit phone number",
          initialValue: "",
          digitsOnly: true,
          maxLength: 10,
        },
      };
    }
    case "subscribe_push": {
      return {
        messages: ["Setting up push notifications for you..."],
        chips: [],
        waiting: true,
      };
    }
    case "check_telegram": {
      return {
        messages: ["You're all set on Telegram — I'll ping you there!"],
        chips: [],
        autoAdvance: true,
      };
    }
    case "ask_special_note": {
      return {
        messages: ["Any special requests? (totally optional)"],
        chips: [{ label: "Skip", value: "__skip__", action: "skip" }],
        input: {
          placeholder: "Special notes (optional)",
          initialValue: "",
          maxLength: 100,
        },
      };
    }
    case "summary": {
      return {
        messages: [{ text: formatSummary(state, ctx), card: true }],
        chips: [
          { label: "Place order", value: "__confirm__", action: "confirm" },
          { label: "Start over", value: "__restart__", action: "restart" },
        ],
      };
    }
    case "submitting": {
      return {
        messages: ["Placing your order now..."],
        chips: [],
        waiting: true,
      };
    }
    case "done": {
      return {
        messages: ["You're all set! Your order is on its way 🎉"],
        chips: [
          { label: "Make another order", value: "__restart__", action: "restart" },
        ],
      };
    }
    default:
      return { messages: [], chips: [] };
  }
};

export const labelForChip = (stepId, value, ctx, runtime) => {
  if (value === "__text__") return "Type my order";
  if (value === "__voice__") return "Order by voice";
  if (value === "__skip__") return "Skip";
  if (value === "__confirm__") return "Place order";
  if (value === "__restart__") return "Start over";
  if (value === "__repeat__") return "Repeat last order";
  if (value === "__new__") return "Make a new order";
  switch (stepId) {
    case "pick_station":
      return getStationById(ctx, value)?.name || String(value);
    case "pick_sub_station": {
      for (const st of ctx.stations || []) {
        const found = (st.sub_stations || []).find((s) => +s.id === +value);
        if (found) return found.name;
      }
      return String(value);
    }
    case "pick_dining":
      return getDiningById(ctx, value)?.name || String(value);
    case "pick_menu_group": {
      const groups = getMenuGroups(ctx);
      if (Array.isArray(value)) {
        return value
          .map((v) => getItemById(groups, v)?.item?.name)
          .filter(Boolean)
          .join(", ");
      }
      return getItemById(groups, value)?.item?.name || String(value);
    }
    case "ask_allergies": {
      if (Array.isArray(value)) {
        return value.map((v) => allergiesNameMapper[v] || v).join(", ");
      }
      return allergiesNameMapper[value] || String(value);
    }
    case "ask_notification_type":
      return NOTIFICATION_LABELS[value] || cap(value);
    default:
      return String(value);
  }
};

export const applyChipAction = (stepId, value, ctx, state, runtime) => {
  switch (stepId) {
    case "ask_order_mode": {
      const voice = value === "__voice__";
      return {
        state,
        runtime: {
          ...runtime,
          orderMode: voice ? "voice" : "text",
          voiceMode: voice,
        },
      };
    }
    case "welcome": {
      if (value === "__repeat__") {
        const nextState = applyRepeatSuggestion(state, ctx.suggestion);
        return {
          state: nextState,
          runtime: {
            ...runtime,
            flow: "repeat",
            allergiesConfirmed: true,
          },
        };
      }
      return { state, runtime: { ...runtime, flow: "new" } };
    }
    case "pick_station":
      return {
        state: { ...state, food_station_id: value },
        runtime: { ...runtime, menuGroupIndex: 0 },
      };
    case "pick_sub_station":
      return { state: { ...state, sub_station_id: value }, runtime };
    case "pick_dining":
      return { state: { ...state, dining_option_id: value }, runtime };
    case "pick_menu_group": {
      const groups = getMenuGroups(ctx);
      const group = groups[runtime.menuGroupIndex];
      const isMulti = !!group?.group?.allow_multiple;
      const newIds = Array.isArray(value) ? value : [value];
      return {
        state: {
          ...state,
          options_id: [...(state.options_id || []), ...newIds],
        },
        runtime: { ...runtime, menuGroupIndex: runtime.menuGroupIndex + 1 },
      };
    }
    case "confirm_allergies":
      if (value === "__yes__") {
        return {
          state,
          runtime: { ...runtime, allergiesConfirmed: true },
        };
      }
      // __change__
      return {
        state: { ...state, allergies: null },
        runtime: { ...runtime, allergiesConfirmed: true },
      };
    case "ask_allergies": {
      const picked = Array.isArray(value) ? value : [value];
      const normalized =
        picked.length === 0 ? ["none"] : picked.includes("none") ? ["none"] : picked;
      return {
        state: { ...state, allergies: normalized },
        runtime: { ...runtime, allergiesConfirmed: true },
      };
    }
    case "confirm_name":
      if (value === "__use_default__") {
        return { state, runtime: { ...runtime, nameConfirmed: true } };
      }
      return {
        state: { ...state, name: null },
        runtime: { ...runtime, nameConfirmed: true },
      };
    case "confirm_surname":
      if (value === "__use_default__") {
        return { state, runtime: { ...runtime, surnameConfirmed: true } };
      }
      return {
        state: { ...state, surname: null },
        runtime: { ...runtime, surnameConfirmed: true },
      };
    case "ask_notification_type":
      return {
        state: { ...state, notification: { type: value } },
        runtime: { ...runtime, emailExplicit: false, phoneExplicit: false },
      };
    case "confirm_notification_email":
      if (value === "__use_default__") {
        return {
          state: {
            ...state,
            notification: {
              ...(state.notification || {}),
              email: ctx.customer?.email || "",
            },
          },
          runtime,
        };
      }
      return { state, runtime: { ...runtime, emailExplicit: true } };
    case "confirm_notification_phone":
      if (value === "__use_default__") {
        const raw = ctx.customer?.phone || "";
        return {
          state: {
            ...state,
            notification: {
              ...(state.notification || {}),
              phone: raw,
            },
          },
          runtime,
        };
      }
      return { state, runtime: { ...runtime, phoneExplicit: true } };
    case "ask_special_note":
      if (value === "__skip__") {
        return {
          state: { ...state, special_note: "" },
          runtime: { ...runtime, specialNoteAsked: true },
        };
      }
      return { state, runtime };
    default:
      return { state, runtime };
  }
};

export const applyTextInput = (stepId, text, ctx, state, runtime) => {
  const trimmed = (text || "").trim();
  switch (stepId) {
    case "ask_name":
      return {
        state: { ...state, name: trimmed },
        runtime: { ...runtime, nameConfirmed: true },
      };
    case "ask_surname":
      return {
        state: { ...state, surname: trimmed },
        runtime: { ...runtime, surnameConfirmed: true },
      };
    case "ask_notification_email":
      return {
        state: {
          ...state,
          notification: { ...(state.notification || {}), email: trimmed },
        },
        runtime,
      };
    case "ask_notification_phone": {
      const digits = trimmed.replace(/\D/g, "").slice(0, 10);
      return {
        state: {
          ...state,
          notification: { ...(state.notification || {}), phone: digits },
        },
        runtime,
      };
    }
    case "ask_special_note":
      return {
        state: { ...state, special_note: trimmed },
        runtime: { ...runtime, specialNoteAsked: true },
      };
    default:
      return { state, runtime };
  }
};

export const validateTextInput = (stepId, text) => {
  const trimmed = (text || "").trim();
  switch (stepId) {
    case "ask_name":
    case "ask_surname":
      if (!trimmed) return "Please enter at least one character.";
      if (trimmed.length > 256) return "Too long (max 256 characters).";
      return null;
    case "ask_notification_email":
      if (!trimmed) return "Please enter your email.";
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmed))
        return "Invalid email format.";
      return null;
    case "ask_notification_phone": {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length !== 10) return "Phone must be 10 digits.";
      return null;
    }
    case "ask_special_note":
      if (trimmed.length > 0 && trimmed.length < 5) return "Please enter at least 5 characters.";
      if (trimmed.length > 100) return "Max 100 characters.";
      return null;
    default:
      return null;
  }
};

export const buildOrderPayload = (state, subdomain, customerId) => {
  const payload = {
    subdomain,
    food_station_id: +state.food_station_id,
    dining_option_id: +state.dining_option_id,
    options_id: (state.options_id || []).map((id) => +id),
    name: state.name,
    surname: state.surname,
    special_note: state.special_note || null,
    allergies: state.allergies?.length ? state.allergies : ["none"],
    notification: { ...(state.notification || {}) },
  };
  if (customerId != null) payload.customer_id = String(customerId);
  if (state.sub_station_id != null) {
    payload.sub_station_id = +state.sub_station_id;
  }
  if (payload.notification.type === "text" && payload.notification.phone) {
    const raw = String(payload.notification.phone);
    if (!raw.startsWith("+")) {
      const digits = raw.replace(/\D/g, "").slice(0, 10);
      payload.notification.phone = `+1${digits}`;
    }
  }
  return payload;
};
